import { ref, watch, type Ref, isRef, unref } from "vue";
import { track } from "jiffy-analytics";
import type { printOnDemand } from "common/utils/models";
import { generatePreview } from "@design-lab/features/manage-product-sizing/ui/generatePreview";

interface Placement {
  artwork: string;
  artworkWidth: number;
  artworkHeight: number;
  surface: printOnDemand.getProduct.SurfacesBack;
  position?: string; // Add position to the interface
}

interface CompositePreview {
  placements?: Placement[];
}

interface UseCompositePreviewReturn {
  frontImage: Ref<string | null>;
  backImage: Ref<string | null>;
  previewIsLoading: Ref<boolean>;
  previewError: Ref<string | null>;
  generateCompositePreview: () => Promise<void>;
}

/**
 * Composable for generating composite preview images
 * @param compositePreview - The composite preview object containing placements
 * @returns Preview state and methods
 */
export function useCompositePreview(
  compositePreview: Ref<CompositePreview | null> | CompositePreview | null
): UseCompositePreviewReturn {
  const frontImage = ref<string | null>(null);
  const backImage = ref<string | null>(null);
  const previewIsLoading = ref(false);
  const previewError = ref<string | null>(null);

  const compositePreviewRef = isRef(compositePreview) ? compositePreview : ref(compositePreview);

  /**
   * Generate preview images from composite preview placements
   */
  const generateCompositePreview = async (): Promise<void> => {
    const preview = unref(compositePreviewRef);

    if (!preview?.placements?.length) {
      frontImage.value = null;
      backImage.value = null;
      return;
    }

    const { placements } = preview;

    previewIsLoading.value = true;
    previewError.value = null;

    try {
      const previewsWithPosition = await Promise.all(
        placements.map(async placement => ({
          position: placement.position || "front",
          preview: await generatePreview({
            imageUrl: placement.artwork,
            artworkSize: {
              width: Number(placement.artworkWidth),
              height: Number(placement.artworkHeight)
            },
            surface: placement.surface
          })
        }))
      );

      // Reset images
      frontImage.value = null;
      backImage.value = null;

      previewsWithPosition.forEach(({ position, preview: previewData }) => {
        if (!previewData?.base64) return;

        if (position === "back") {
          backImage.value = previewData.base64;
        } else {
          frontImage.value = previewData.base64;
        }
      });
    } catch (err) {
      track("mockup_failed_to_render", {
        error_message: err instanceof Error ? err.message : String(err),
        error_type: err instanceof Error ? err.name : "Unknown",
        component: "useCompositePreview"
      });

      previewError.value =
        "Design preview unavailable. Your design has been saved and will print as uploaded.";
      frontImage.value = null;
      backImage.value = null;
    } finally {
      previewIsLoading.value = false;
    }
  };

  watch(
    compositePreviewRef,
    async newVal => {
      if (newVal) {
        await generateCompositePreview();
      } else {
        // Clear images when preview is null
        frontImage.value = null;
        backImage.value = null;
      }
    },
    { deep: true, immediate: true }
  );

  return {
    frontImage,
    backImage,
    previewIsLoading,
    previewError,
    generateCompositePreview
  };
}
